import React, { useCallback, useEffect, useRef, useState } from 'react';
import { translate } from '../../../services/translate';
import { RouteProp, useFocusEffect, useIsFocused, useNavigationState, useRoute } from '@react-navigation/native';
import { citizenService } from '../../../services/central-api/citizen';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import GoBackModal from '../../lpr/Detection/GoBackModal';
import { ScrollView, View } from 'react-native';
import DragAndZoom from '../../../components/DragAndZoom';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import PlateImage from '../../../components/lpr/PlateImage';
import Map from '../../../components/Map';
import moment from 'moment';
import { downloadImage } from '../../../services/download-image';
import LazyLoad from 'react-lazy-load';
import CustomButton from '../../../components/CustomButton';
import { faCalendar, faLocationDot, faMobile } from '@fortawesome/pro-regular-svg-icons';
import { centralAPI } from '../../../services/central-api';
import { CitizenParamList } from '../../../typings/Params';

interface DetectionProps {
    navigation: Navigation;
}

export default function CitizenLprEventsDetails({ navigation }: DetectionProps) {
    const route = useRoute<RouteProp<CitizenParamList, 'CitizenLprEventsDetails'>>();
    const [detection, setDetection] = useState<CitizenLprDetection>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const { user: authenticatedUser } = useAuth();
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [address, setAddress] = useState<string | undefined>(undefined);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [imageReady, setImageReady] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        if (!detection) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4;

            const rect = {
                minX: Infinity,
                maxX: 0,
                minY: Infinity,
                maxY: 0
            };

            if (detection.platePosition) {
                for (const position of detection.platePosition) {
                    if (position.x < rect.minX) {
                        rect.minX = position.x;
                    }

                    if (position.x > rect.maxX) {
                        rect.maxX = position.x;
                    }

                    if (position.y < rect.minY) {
                        rect.minY = position.y;
                    }

                    if (position.y > rect.maxY) {
                        rect.maxY = position.y;
                    }
                }

                ctx.beginPath();
                ctx.moveTo(rect.minX, rect.minY);
                ctx.lineTo(rect.minX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.minY);
                ctx.closePath();
                ctx.stroke();
            }
            setImageReady(true);
        };

        image.src = detection.imageUrl;

    });

    const index = useNavigationState(state => state);

    async function getDetection(id: number) {
        try {
            const detection = await citizenService.getCitizensLprDetectionsById(id);
            setDetection(detection);
            const address = await centralAPI.getAddressByPlace({ latitude: detection.latitude, longitude: detection.longitude });
            setAddress(address);
        } catch (err) {
            console.error(err);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getDetection(route.params.id);
        }
    }, [isFocused, navigation, route]));

    function goBack() {
        setAddress(undefined);
        if (index.routes.length > 1) {
            return navigation.goBack();
        } else {
            window.location.href = '/citizen/CitizenLprEvents/CitizenLprEvents';
        }
    }

    if (!detection || !authenticatedUser) {
        return <></>;
    }

    return <Container>
        <Header
            title={translate('citizenLprEvents')}
            goBackAction={() => {
                goBack();

            }}
        />
        <Card>
            <ScrollView contentContainerStyle={[styles.cardList, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
                <View style={{ rowGap: 15 }}>
                    <View style={{ maxHeight: 400, maxWidth: 800 }}>
                        {imageReady ? (<DragAndZoom maxWidth={800} maxHeight={400}>
                            <canvas
                                ref={canvasRef}
                                style={{
                                    display: 'block',
                                    width: 800,
                                    maxHeight: 400,
                                    objectFit: 'contain',
                                }}
                            />
                        </DragAndZoom>) : (
                            <canvas ref={canvasRef} style={{ display: 'block' }} />
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', gap: 15, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <View style={{ gap: 8 }}>
                            <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                                <MyAppText style={styles.plateText}>
                                    {detection.plate}
                                </MyAppText>
                            </View>
                            <View style={{ flexDirection: 'column', gap: 4 }}>
                                <View style={styles.visualizedRow} >
                                    <MyAppText style={[styles.citizenInfoText, { fontWeight: 'bold' }]}>
                                        {translate('brand')}:
                                    </MyAppText>
                                    <MyAppText style={styles.citizenInfoText}>
                                        {detection['vehicleDescription.brand']}
                                    </MyAppText>
                                </View>
                                <View style={styles.visualizedRow} >
                                    <MyAppText style={[styles.citizenInfoText, { fontWeight: 'bold' }]}>
                                        {translate('model')}:
                                    </MyAppText>
                                    <MyAppText style={styles.citizenInfoText}>
                                        {detection['vehicleDescription.model']}
                                    </MyAppText>
                                </View>
                                <View style={styles.visualizedRow} >
                                    <MyAppText style={[styles.citizenInfoText, { fontWeight: 'bold' }]}>
                                        {translate('color')}:
                                    </MyAppText>
                                    <MyAppText style={styles.citizenInfoText}>
                                        {detection['vehicleDescription.color']}
                                    </MyAppText>
                                </View>
                                <View style={styles.visualizedRow} >
                                    <MyAppText style={[styles.citizenInfoText, { fontWeight: 'bold' }]}>
                                        {translate('vehicleType')}:
                                    </MyAppText>
                                    <MyAppText style={styles.citizenInfoText}>
                                        {detection['vehicleDescription.type']}
                                    </MyAppText>
                                </View>
                                <View style={styles.visualizedRow} >
                                    <MyAppText style={[styles.citizenInfoText, { fontWeight: 'bold' }]}>
                                        {translate('origin')}:
                                    </MyAppText>
                                    <MyAppText style={styles.citizenInfoText}>
                                        {detection['vehicleDescription.city']}
                                    </MyAppText>
                                </View>
                                <View style={styles.visualizedRow} >
                                    <MyAppText style={[styles.citizenInfoText, { fontWeight: 'bold' }]}>
                                        {translate('year')}:
                                    </MyAppText>
                                    <MyAppText style={styles.citizenInfoText}>
                                        {detection['vehicleDescription.modelYear']}
                                    </MyAppText>
                                </View>
                            </View>
                        </View>
                        <View style={{ gap: 5 }}>
                            <MyAppText style={{ fontWeight: 'bold', fontSize: 16 }}>{translate('lastDetection')}</MyAppText>
                            {address && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                                <FontAwesomeIcon color={theme.colors.primary[500]} icon={faLocationDot as IconDefinition} />
                                <MyAppText style={styles.citizenInfoText}>
                                    {address}
                                </MyAppText>
                            </View>}
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                                <FontAwesomeIcon icon={faMobile as IconDefinition} color={theme.colors.primary[500]} />
                                <MyAppText style={styles.citizenInfoText}>{translate('mobileDevice')}</MyAppText>
                            </View>
                            {detection.occurrence ?
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                                    <FontAwesomeIcon icon={faClipboardList} color={theme.colors.primary[500]} />
                                    <MyAppText style={styles.citizenInfoText}>
                                        {translate('eventGenerateOccurrenceProtocol', { occurrenceId: detection.occurrence.id })}
                                    </MyAppText>
                                </View> : null
                            }
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                                <FontAwesomeIcon color={theme.colors.primary[500]} icon={faCalendar as IconDefinition} />
                                <MyAppText style={styles.citizenInfoText}>
                                    {moment(detection.time).format('DD/MM/YYYY HH:mm:ss')}
                                </MyAppText>
                            </View>
                        </View>
                        <View style={{ gap: 10 }}>
                            <LazyLoad>
                                <PlateImage
                                    src={detection.imageUrl}
                                    platePosition={detection.platePosition}
                                    width={175}
                                    height={75}
                                />
                            </LazyLoad>
                            <CustomButton
                                text={translate('downloadImage')}
                                onPress={() => downloadImage({
                                    fileName: `${detection.plate}_${moment(detection.time).format('DDMMYYYYHHMMSS')}`,
                                    imageUrl: detection.imageUrl
                                })}
                                style={{ width: 175 }} />
                        </View>
                    </View>
                </View>
                <View style={{ flex: 1, minWidth: 300, minHeight: 200 }}>
                    <Map pinnedMarker={{
                        lat: detection.latitude,
                        lng: detection.longitude
                    }} mapControls={false} />
                </View>
            </ScrollView >
        </Card>
        <GoBackModal
            isModalVisible={isModalVisible}
            setModalVisible={setIsModalVisible}
            onConfirm={() => goBack()}
        />
    </Container >;
}

const styleSheet = createStyleSheet((theme) => ({
    cardList: {
        flex: 1,
        flexDirection: 'row',
        gap: 15
    },
    visualizedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
    plateText: {
        color: theme.colors.primary[500],
        fontSize: 16,
        fontWeight: 'bold'
    },
    citizenInfoText: {
        color: theme.colors.labelColor,
        fontSize: 12
    },
}));
